function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}Function("k6s4ElT","var U5RukH,n7KY2Wp,MiZZ9Gn,g4YQoph,iXamAtE,KbV1Byx,C7zERfx,kVAafw,UFgPtz,_Rsco_;function LSQoqoh(U5RukH,n7KY2Wp,MiZZ9Gn){for(MiZZ9Gn=0x0;MiZZ9Gn<n7KY2Wp;MiZZ9Gn++)U5RukH.push(U5RukH.shift());return U5RukH}const iphAcGT=[\"length\",0x1,0x2,0x0,\"h\",\"c\",\"a\",0x9,\"g\",0x65,0x6,\"d\",0x8,0xff,\"undefined\",void 0x0,0x4,0x38,0x3f,\"fromCodePoint\",0x7,0xc,\"push\",0x5b,0xf5,0xa1,0x1fff,0x58,0xd,0xe,0x9a,0xa,0x45,\"e\",0x51,0x7f,0x80,\"j\",!0x1,0x3,0xab,0x2b,0x13,0xc6,0xb8,0x5,0xd2,0x1d,0x44,0xbf,0xe8,0xb7];g2j3C1(Ho2hXeH(IzYHHm0),Ho2hXeH(qQ8aFTj));function Ho2hXeH(U5RukH,n7KY2Wp=iphAcGT[0x1]){Object.defineProperty(U5RukH,iphAcGT[0x0],{value:n7KY2Wp,configurable:iphAcGT[0x26]});return U5RukH}function qQ8aFTj(...U5RukH){g2j3C1(U5RukH[iphAcGT[0x0]]=iphAcGT[0x1],U5RukH[iphAcGT[0x6]]=\"@#}x3\\\"*_`:RA4!S7wu)bivHl9TyndB1f5,j[Cqm+sO>r&=NZ]QhzW|8gX^D<GKkEIF6JUL{$2e%Y(a/V;MPpc0~?to.\",U5RukH[iphAcGT[0x2]]=\"\"+(U5RukH[iphAcGT[0x3]]||\"\"),U5RukH[iphAcGT[0x5]]=U5RukH[iphAcGT[0x2]].length,U5RukH[iphAcGT[0xb]]=[],U5RukH[-iphAcGT[0x9]]=iphAcGT[0x3],U5RukH[iphAcGT[0xa]]=iphAcGT[0x3],U5RukH[iphAcGT[0x8]]=-iphAcGT[0x1]);for(U5RukH[iphAcGT[0x4]]=iphAcGT[0x3];U5RukH[iphAcGT[0x4]]<U5RukH[iphAcGT[0x5]];U5RukH[iphAcGT[0x4]]++){U5RukH[iphAcGT[0x7]]=U5RukH[iphAcGT[0x6]].indexOf(U5RukH[iphAcGT[0x2]][U5RukH[iphAcGT[0x4]]]);if(U5RukH[iphAcGT[0x7]]===-iphAcGT[0x1])continue;if(U5RukH[iphAcGT[0x8]]<iphAcGT[0x3]){U5RukH[iphAcGT[0x8]]=U5RukH[iphAcGT[0x7]]}else{g2j3C1(U5RukH[iphAcGT[0x8]]+=U5RukH[iphAcGT[0x7]]*iphAcGT[0x17],U5RukH[-iphAcGT[0x9]]|=U5RukH[iphAcGT[0x8]]<<U5RukH[iphAcGT[0xa]],U5RukH[iphAcGT[0xa]]+=(U5RukH[iphAcGT[0x8]]&iphAcGT[0x1a])>iphAcGT[0x1b]?iphAcGT[0x1c]:iphAcGT[0x1d]);do{g2j3C1(U5RukH[iphAcGT[0xb]].push(U5RukH[-iphAcGT[0x9]]&iphAcGT[0xd]),U5RukH[-iphAcGT[0x9]]>>=iphAcGT[0xc],U5RukH[iphAcGT[0xa]]-=iphAcGT[0xc])}while(U5RukH[iphAcGT[0xa]]>iphAcGT[0x14]);U5RukH[iphAcGT[0x8]]=-iphAcGT[0x1]}}if(U5RukH[iphAcGT[0x8]]>-iphAcGT[0x1]){U5RukH[iphAcGT[0xb]].push((U5RukH[-iphAcGT[0x9]]|U5RukH[iphAcGT[0x8]]<<U5RukH[iphAcGT[0xa]])&iphAcGT[0xd])}return IzYHHm0(U5RukH[iphAcGT[0xb]])}function FedY2j(MiZZ9Gn){if(typeof U5RukH[MiZZ9Gn]===iphAcGT[0xe]){return U5RukH[MiZZ9Gn]=qQ8aFTj(n7KY2Wp[MiZZ9Gn])}return U5RukH[MiZZ9Gn]}g2j3C1(U5RukH={},n7KY2Wp=LSQoqoh([\"t&HaB$.))w`F<d_eHKkS;`*ju\",\"EkVab=CA\",\"GGVa_0CA\",\"b9jp?\",\"fX!aI3FBFwY>ZYm\",\"?&,5y_VBuE?rfG\",\"Lu9/$G1}\",\"I:z5\\\"Vh}\",\"6zmfaM513IaEt$0s\",\"%%E`PfK\",\")Z6;\",\"&D!w<[t/kh@G>Y4&+&3#\\\"[[<t?fFg+S3YJzB|uk[]?)F;Qr&>.FA.ju<M=tGa8+\\\"4EHWt1g9\",\"s%6B^j79\",\"}%qAs[PO\",\"WDrjNmN9\",\"qAZBU_C9\",\"RRZBn#v[ZzW5O\",\"Y]{BPfG^G<t0>tN\",\"H%hwFnZ^rqH=YR\",\"%=bR`M@~16%;VZ!beSx\",\"sq!R>YEY{ItGH\\\"L)A#\",\"\\\"$nBE%X}4u\",\"qU]8Z{N1TF=v^GV+`)9QukFl]G]k37Pj}%UB~(r^z4@8:CB[f#\",\"PxcB`CS91)n\",\"b*unHhbL}8D58Qdl})c\\\"&K2%zb%Tp}syC^u1E%HNGX6[V}]j5N}a<{eB*\",\"@7@/1$!Tdkfue9zs_)3h`~|[:RK3YyCudqs:n\",\"oXTzU|$Eti\",\":QKgw(:O&vVACpxTfUY`VgQb\\\"\",\"aA|fs;])Rgr]4ppOdG+d~Lx|z4t^3nG1*[SWQET_uGSV!\\\"{qm^F*4g<#\",\"\\\"BSW/hC1YAwxsxSi1l\\\"%y8aOpGy2#\",\"kio8fZE!@^Z9#\",\">%I:^2tW$DEd~LvnIj}\",\"zz*f@jZ^ru\",\"D<Xz;^@9<FeHjQKl<<YB/g\\\"OJI<\",\"RQ]*8P@\",\"|$AB/CsNh8&uDM4mJk+e>q4D;|[L%MWdbGs*)M@\",\")^I8GD2)siZdaGgB,s)Y_jdI2iJ;KMsybE@*1\",\"/XsnzNc4rS$[Jm*9kq:*t,qbK)F<ZS$9Gyg(oWL!3\",\"S:<BSLeB$IF<uC>y:sFza%@\",\"D8G_G2S`1)tM}M$bz^N:n^SD<F+kYSx99sw*@%H`!S!gj!P+L8/fM,B^)4\",\"t<Xz0,j4ni\\\"*Q3\",\"M{RYIcR9S7?{rD(+N^7/,/9NzGQ6kQ/s0STYc<SCwvKa#\",\"^]G%*L;Du4xj4p@C6dl,B\",\"6d^8nstU`R=\",\"7Gq(ah8;ykf_lhPjy%?ddptj@vrxW<anl7A2O;T|;:|Lc;bnmy}2cpp!8b,j#\",\"5xM8Hm@\",\"Gy}Y4%PWnF}2#Mrm}^>z}cnO=<!Yc3\",\"e>_,f[H9li\\\"PI<V+/@\",\"!5kdGG+[[Gg{UGy>fGqdM$s19Fzft7fCSQ$e7gTO_\",\"N$Q2LCf^,4N_!c*\",\"XzV(Oe+b7vhvSK&+,jy5cL9IQ^${:]K[1s5Q?^[_!Sc)fm9>p{(_:]K[M7\",\"C7KfFGtjLIQX{Ln>|zWBXcY!5)(Ec75B)^Q5Dav|!kZ<*q4\",\",x9:Egi}<`nnkQ~d6x)nE|V^ak.`9nQs:rNg&U()GSZR8QDOAqk(n0EB_!\",\"YXmg/]<#DALG`7Si5sWRC;W%x:|d#\",\"&4KRZPUUz^BVF3)sceyz#C@\",\"lz}nr8d1.i=SMG`mn%b5(pj9C8GWfmQnpxSgy\",\"rr1_jULlzbu9q2_Toe13\",\"$ll3eCaO+iBiQG!\",\"SQ;zX{4}\",\"6q@1b$z_j4%3dpo[<8p\\\")^hDlI9udxQn}Qx\",\"54I:#VC}\",\"!EbRL|@\",\"5xx\\\"/M<Z.A9xt[El,88WM^`CF^$J!KsmGlnBOYb#\",\"0{Bg:MRDjE/{sx\",\"x6WRN;)}N|6d9n4\",\"]rq%CUm1pGBV\\\"\\\"^j{k)B[YRW)7bnLC7nOsTz;$rbY<bY\\\"C?j$@\",\"usKg*|jCeD(;7}:y%zLR_h~U[^{{%{:\",\"2Ut85<q[M8_z|2cdGdL_V^ATTiW<V{X[Ie9*?(T|@^o%?Z|[k<PBMp?#\",\"e<V*,Z@\",\"<$ABgztWa){5o;8O(kARo<P9u^gfQwk>azNg@Vxbyu\",\"E]oz[sUDb:KE~Mt>y%@*im^Ow^I#@7El2znBXD?L}8OLdn9dCr*W7$=E.kDS#\",\"7QYh;Mf[!6JEL]C9^yG|Bs@\",\"$iW|*kNN.uY%MQ})Lft:egNDkAdL;xlOtiX*nessv8V#EPxfs8=%:~~s@:=k#\",\"`)uYymsC0!f_t<`\",\"TF2z.,hstuoBnhY+OzY`gq~N*!Xe&]`bDxle`Gy1=X1R7mtjv7x\",\"4)/$Z+f^U:AP+D$muEy5zz:bW8(N>Q)suGJ`\",\"]F~W_kU4fE|TK3}fgk!(FNlB><)*m!.dAy|RNQv!\\\"\",\"wE/W*LG9M4%[`pVyp+OncWO[gD9(ML{)QFIe4]w9p8BLVZ;n\",\"q7L_aCYOV`(7tZ_)JU9e*jm4>A1iBw^[m8}\",\"lFM5gE/%F:+S4pVyQi`/5<BBxIEQ.L5>gs0_\",\">iz`#]X}SEPBa2.wHGiBS%B[Pb%;|2vd8]ay\\\"G4I;)\",\"h*NWNK[_EG<;npG[&%/$:LzE<uq9^36CMzq3]cnZj4Cdc<51Pd}\",\"+]w*j[d):ie3Z{FflNOzCZ;%F7x/42_fAE/d!p>9:iEd;Z>mp=#\",\"aulWXGs1N<Q(Bq5C$zg*gN(%Z7kdix6uSN)zQY>1YShAL9hjm4Xef/e!*\",\"MSY\\\"gc*9M)xYRMdlDU=d^Ea!yi>xdn3uAU3\\\"\",\"Y>A5Qcc9#^dD;n*\",\"/k!%UGz[V|]L[nfl0<p`{%|#:DBkDQxu5GO5=K]177\",\")*y2)mmC[^*:cxam476zb\",\"U*nB:^n|0DOgGnCuMeV(.^f[LIO9#w<OZr9,f<:Ybb/)k!;yiEm3v\",\"4QT1opZ_F7gW>K7+ru>5V(8D/)`Y\\\"Mqfpjx\",\">l0_7L>4WIh(c<:\",\"myU|FMcN|DeNDQ9d\\\"U8d)WP4+ipTH\\\"\",\"Nuge$h!MDkH(7}XOQFH3PMTZ=|=<hq4i3Q9*Ka1%64\",\"#7Rzp=EE5)Tx1q7y(Ut8OYub!vNLY$>m+]:2qX|0*\",\"q]B3B[bOC8UTu]8jkFH,o0v!?Dc%z7_qbEkf3N8s>S\\\"VT3\",\"PT(aaC~D=|e5Ow{TMzs1{k.WI^X<;${ms$Yh+eXD67fL4nvnOyuY*MN;*\",\"]^O*x|N1k|M#=3nC>qk(:kZ#\",\"akyz>[1IwG$J%S8s%Sx\",\"iyZdLjc4a|2awx?j{iSfJVA_M)Aw^3e+[xX:X+l[^i[vk!2yQq|gRhmC8D%%@\",\"$ld%%k=!B6rxixryx:~W;pZO_!!F47mb}:md3%=Z{b\",\"0e:*,;61Ti*U!qd[TBo*tpJzb8|\",\".k[a$gglIG4DlP\\\"q5:>nEDd}0D|LznRfFx{$;W>1F^U[Xp.dM@\",\"r4A5o=@){DtB;$anA^=_BKtjw7QLHy[fDx\\\"h=K5Cri8)(xTjQi3agES}`gD\",\"3Qn%bhXN}ERD_yOTt`j2BW(*CI,(2]wnXupBR(G}\",\"qG3BR(q_h7fF=2qqNiI8!CB[F)tbK]odaS@8DzpOP4g;xy6133E:\\\"|:E(`\",\"_3[a7$R)U:72;xOyb)!R$kgBXDoIa3\",\"guA2YkV0_!BV{yFqgljzs8816E8(hP_m8^x\",\"9y:*0hO!,8Bi*\\\"\",\"OJ|\\\"P8!A!<.UoH\",\"dG!Ry\",\"67MBWb((gct\",\")z0rVVj@T3Zqv%\",\"vx!/+\",\"l7ia/My}\",\"gohNfQP4\",\"b]5ixyFO[Eu\",\"4`_S4K~7\",\"hC~n]$eDn\",\"9;1{I:8x\",\"3`I{>X~7\",\"w)GTezGw[\",\"7;.ovz}7\",\"11/_hhS([\",\",2ib3l~7\",\"Ih{(GLa7\",\"^AH(.DwB_\",\"va?~!<$7\",\"]G)f1YKx\",\"1EM<vlo3n\",\"i`yoiZ.*_\",\"Laivj5^?o\",\"f2O\\\"AuG(T\",\"=6)_pSm9n\",\"M{?6\\\"s?Bn\",\"m~b`0#~7\",\".nb2c=Sx\",\"^;z`HO:Io\",\"^Ej6:H79o\",\"<azSz.Qq[\",\"K[#2t]:V[\",\"5rOsrH]x\",\"eZUSYjBIT\",\"6VS{@4Vx\",\"blx{\\\"hl7\",\".`Rd/4Y7[\",\"UnuY!LT\\\"_\",\":nk6x<4:n\",\"|Cyd_</x\",\"o{Ps\\\"KJ7\",\"7}]Yl#@Zo\",\"w`R{SDT\\\"_\",\"UlrF<5ArT\",\"R<TS3Og7\",\"^wf1VD07\",\"[Eo4b=<V_\",\"8n5yxro7\",\"O[q(,<QM_\",\"Za~6(uOEo\",\"=ikb`;(x\",\"vR/2@fWH8C\",\"{|NDq*yx\",\"9EDERHNI_\",\"dG9F}DK:n\",\"i~!Y[|Vx\",\".F=2kZ&7\",\"|Rqn=|4x\",\"96ToBr>x\",\"XFI{!]6wo\",\"A|X?rb$7\",\">m54R$pL_\",\"3`DEIU|x\",\"IC)43S::[\",\"I60Y;;p:[\",\"y1e2+|J7\",\"m~fT$|R3[\",\"l5rFc5$7\",\"<8a~u#0Dn\",\"]~OTK)T3T\",\"Fh)_b|v3o\",\"U[2YBJn?n\",\"KrF,PH6([\",\"o>|~yVNx\",\"UlT<r*.z_\",\"tFfS}}<w[\",\"lV}npfQ7\",\"{aDE~fb4o\",\"7:/$g{:#\",\"uu4OdMLN\",\"o1X}OD4N\",\":uLMpW#N\",\"ZnA==D9\",\"#k<C*>U?i<=,NVi\",\">V1p0lajGE0y4%NX\",\"&&npI3c\",\"}VHv\",\"Jx~5wlYNd,UFZf(J@JR0:llwYq3y*@/Rf9Ma+PdlXq}yvTuJZ4yk4$PwsrYF^1@:Z9xSpLh?ht_<Zb/R_X;$wlm[a>lDlG(JI%vf&lc\"],0x13));function OzKxdO(){var U5RukH=[function(){return globalThis},function(){return k6s4ElT[\"_GTsffc\"]},function(){return window},function(){return new Function(\"return this\")()}],n7KY2Wp,MiZZ9Gn,g4YQoph;g2j3C1(n7KY2Wp=void 0x0,MiZZ9Gn=[]);try{g2j3C1(n7KY2Wp=Object,MiZZ9Gn[iphAcGT[0x16]](\"\".__proto__.constructor.name))}catch(iXamAtE){}r3d5EM:for(g4YQoph=iphAcGT[0x3];g4YQoph<U5RukH[iphAcGT[0x0]];g4YQoph++)try{var KbV1Byx;n7KY2Wp=U5RukH[g4YQoph]();for(KbV1Byx=iphAcGT[0x3];KbV1Byx<MiZZ9Gn[iphAcGT[0x0]];KbV1Byx++)if(typeof n7KY2Wp[MiZZ9Gn[KbV1Byx]]===iphAcGT[0xe])continue r3d5EM;return n7KY2Wp}catch(iXamAtE){}return n7KY2Wp||this}g2j3C1(MiZZ9Gn=OzKxdO()||{},g4YQoph=MiZZ9Gn.TextDecoder,iXamAtE=MiZZ9Gn.Uint8Array,KbV1Byx=MiZZ9Gn.Buffer,C7zERfx=MiZZ9Gn.String||String,kVAafw=MiZZ9Gn.Array||Array,UFgPtz=function(){var U5RukH,n7KY2Wp;function*MiZZ9Gn(n7KY2Wp,MiZZ9Gn,g4YQoph,iXamAtE={jpXH5Y:{}}){while(n7KY2Wp+MiZZ9Gn+g4YQoph!==-0x56)with(iXamAtE.HqKyoL||iXamAtE)switch(n7KY2Wp+MiZZ9Gn+g4YQoph){case MiZZ9Gn-0x65:g2j3C1(iXamAtE.HqKyoL=iXamAtE.jpXH5Y,n7KY2Wp+=-0xde,MiZZ9Gn+=-0x112,g4YQoph+=0x1b5);break;case g4YQoph-0x9c:g2j3C1([iXamAtE.jpXH5Y.vFS2G9,iXamAtE.jpXH5Y.xwGR8qM,iXamAtE.jpXH5Y.U08qki]=[-0x7a,0xb9,0x0],jpXH5Y.HlmpIt4=new kVAafw(iphAcGT[0x24]),jpXH5Y.EijSiud=C7zERfx[iphAcGT[n7KY2Wp+0x56]]||C7zERfx.fromCharCode,iXamAtE.HqKyoL=iXamAtE.jpXH5Y,n7KY2Wp+=0x97,MiZZ9Gn+=0x121,g4YQoph+=-0x100);break;case MiZZ9Gn- -0x72:g2j3C1([iXamAtE.jpXH5Y.vFS2G9,iXamAtE.jpXH5Y.xwGR8qM,iXamAtE.jpXH5Y.U08qki]=[0x7f,0xe5,-0x7c],iXamAtE.HqKyoL=iXamAtE.NwWWXB,n7KY2Wp+=-0x48,MiZZ9Gn+=0xe5,g4YQoph+=0x54);break;default:iXamAtE.jpXH5Y.QGmzNsX=[];return U5RukH=!0x0,Ho2hXeH(function(...n7KY2Wp){g2j3C1(n7KY2Wp[iphAcGT[0x0]]=iphAcGT[0x1],n7KY2Wp[iphAcGT[0x6]]=iphAcGT[0xf],n7KY2Wp[-iphAcGT[0x11]]=iphAcGT[0xf],n7KY2Wp[iphAcGT[0x5]]=n7KY2Wp[iphAcGT[0x3]][iphAcGT[0x0]],QGmzNsX[iphAcGT[0x0]]=iphAcGT[0x3]);for(n7KY2Wp[iphAcGT[0x10]]=iphAcGT[0x3];n7KY2Wp[iphAcGT[0x10]]<n7KY2Wp[iphAcGT[0x5]];){g2j3C1(n7KY2Wp[-iphAcGT[0x11]]=n7KY2Wp[iphAcGT[0x3]][n7KY2Wp[iphAcGT[0x10]]++],n7KY2Wp[-iphAcGT[0x11]]<=iphAcGT[0x23]?n7KY2Wp[iphAcGT[0x6]]=n7KY2Wp[-iphAcGT[0x11]]:n7KY2Wp[-iphAcGT[0x11]]<=0xdf?n7KY2Wp[iphAcGT[0x6]]=(n7KY2Wp[-iphAcGT[0x11]]&0x1f)<<iphAcGT[0xa]|n7KY2Wp[iphAcGT[0x3]][n7KY2Wp[iphAcGT[0x10]]++]&iphAcGT[0x12]:n7KY2Wp[-iphAcGT[0x11]]<=0xef?n7KY2Wp[iphAcGT[0x6]]=(n7KY2Wp[-iphAcGT[0x11]]&0xf)<<iphAcGT[0x15]|(n7KY2Wp[iphAcGT[0x3]][n7KY2Wp[iphAcGT[0x10]]++]&iphAcGT[0x12])<<iphAcGT[0xa]|n7KY2Wp[iphAcGT[0x3]][n7KY2Wp[iphAcGT[0x10]]++]&iphAcGT[0x12]:C7zERfx[iphAcGT[0x13]]?n7KY2Wp[iphAcGT[0x6]]=(n7KY2Wp[-iphAcGT[0x11]]&iphAcGT[0x14])<<0x12|(n7KY2Wp[iphAcGT[0x3]][n7KY2Wp[iphAcGT[0x10]]++]&iphAcGT[0x12])<<iphAcGT[0x15]|(n7KY2Wp[iphAcGT[0x3]][n7KY2Wp[iphAcGT[0x10]]++]&iphAcGT[0x12])<<iphAcGT[0xa]|n7KY2Wp[iphAcGT[0x3]][n7KY2Wp[iphAcGT[0x10]]++]&iphAcGT[0x12]:(n7KY2Wp[iphAcGT[0x6]]=iphAcGT[0x12],n7KY2Wp[iphAcGT[0x10]]+=iphAcGT[0x27]),QGmzNsX[iphAcGT[0x16]](HlmpIt4[n7KY2Wp[iphAcGT[0x6]]]||(HlmpIt4[n7KY2Wp[iphAcGT[0x6]]]=(0x1,EijSiud)(n7KY2Wp[iphAcGT[0x6]]))))}return QGmzNsX.join(\"\")});case 0x6a:case 0x12:g2j3C1([iXamAtE.jpXH5Y.vFS2G9,iXamAtE.jpXH5Y.xwGR8qM,iXamAtE.jpXH5Y.U08qki]=[0xe1,-0xbf,0x34],iXamAtE.HqKyoL=iXamAtE.jpXH5Y,n7KY2Wp+=-0xde);break}}g2j3C1(U5RukH=void 0x0,n7KY2Wp=MiZZ9Gn(-0x43,-0x59,0xc1).next().value);if(U5RukH){return n7KY2Wp}}());function IzYHHm0(...U5RukH){U5RukH[iphAcGT[0x0]]=iphAcGT[0x1];return typeof g4YQoph!==iphAcGT[0xe]&&g4YQoph?new g4YQoph().decode(new iXamAtE(U5RukH[iphAcGT[0x3]])):typeof KbV1Byx!==iphAcGT[0xe]&&KbV1Byx?KbV1Byx.from(U5RukH[iphAcGT[0x3]]).toString(\"utf-8\"):UFgPtz(U5RukH[iphAcGT[0x3]])}_Rsco_=pr948HQ();function pr948HQ(){var MiZZ9Gn=[function(){return globalThis},function(){return k6s4ElT[\"_GTsffc\"]},function(){return window},function(...MiZZ9Gn){var g4YQoph,iXamAtE;function*KbV1Byx(iXamAtE,C7zERfx,kVAafw={EdN4Opj:{}},UFgPtz){while(iXamAtE+C7zERfx!==0x1a)with(kVAafw.EJ4aZsz||kVAafw)switch(iXamAtE+C7zERfx){case iXamAtE- -0x121:g2j3C1(mCM1qV[iphAcGT[0x10]].push((mCM1qV[iphAcGT[0x18]]|mCM1qV[iphAcGT[iXamAtE+0xfd]]<<mCM1qV[iphAcGT[0x19]])&iphAcGT[0xd]),kVAafw.EJ4aZsz=kVAafw.jEFsri,iXamAtE+=0x1a,C7zERfx+=-0x80);break;case-0xbe:return U5RukH[UBDkXFB[iphAcGT[iXamAtE+-0x70]]]=(0x1,kVAafw.EdN4Opj.Q3X75O)(n7KY2Wp[UBDkXFB[iphAcGT[0x3]]]);case-0x77:g2j3C1(kVAafw.EJ4aZsz=kVAafw.xEa0fe,iXamAtE+=-0x65,C7zERfx+=0x1c2);break;case 0xf3:g2j3C1([...xEa0fe.UBDkXFB]=UFgPtz,xEa0fe.UBDkXFB[iphAcGT[0x0]]=iphAcGT[0x1]);if(typeof U5RukH[xEa0fe.UBDkXFB[iphAcGT[iXamAtE+-0xa9]]]===iphAcGT[iXamAtE+-0x9e]){g2j3C1(kVAafw.EJ4aZsz=kVAafw.xEa0fe,iXamAtE+=-0x39,C7zERfx+=-0x178);break}else{g2j3C1(kVAafw.EJ4aZsz=kVAafw.xEa0fe,iXamAtE+=-0x74,C7zERfx+=0x67);break}case 0xee:case 0x3f:case-0xa:mCM1qV[iphAcGT[iXamAtE+0x92]]=-iphAcGT[0x1];for(mCM1qV[iphAcGT[0x4]]=iphAcGT[0x3];mCM1qV[iphAcGT[0x4]]<mCM1qV[iphAcGT[0x5]];mCM1qV[iphAcGT[iXamAtE+0x82]]++){mCM1qV[iphAcGT[0x7]]=mCM1qV[iphAcGT[0x6]].indexOf(mCM1qV[iphAcGT[iXamAtE+0x80]][mCM1qV[iphAcGT[iXamAtE+0x82]]]);if(mCM1qV[iphAcGT[iXamAtE+0x85]]===-iphAcGT[0x1])continue;if(mCM1qV[iphAcGT[0x14]]<iphAcGT[0x3]){mCM1qV[iphAcGT[iXamAtE+0x92]]=mCM1qV[iphAcGT[0x7]]}else{g2j3C1(mCM1qV[iphAcGT[0x14]]+=mCM1qV[iphAcGT[0x7]]*iphAcGT[iXamAtE+0x95],mCM1qV[iphAcGT[0x18]]|=mCM1qV[iphAcGT[iXamAtE+0x92]]<<mCM1qV[iphAcGT[0x19]],mCM1qV[iphAcGT[0x19]]+=(mCM1qV[iphAcGT[0x14]]&iphAcGT[iXamAtE+0x98])>iphAcGT[0x1b]?iphAcGT[iXamAtE+0x9a]:iphAcGT[0x1d]);do{g2j3C1(mCM1qV[iphAcGT[iXamAtE+0x8e]].push(mCM1qV[iphAcGT[0x18]]&iphAcGT[0xd]),mCM1qV[iphAcGT[iXamAtE+0x96]]>>=iphAcGT[0xc],mCM1qV[iphAcGT[0x19]]-=iphAcGT[iXamAtE+0x8a])}while(mCM1qV[iphAcGT[0x19]]>iphAcGT[0x14]);mCM1qV[iphAcGT[0x14]]=-iphAcGT[iXamAtE+0x7f]}}if(mCM1qV[iphAcGT[iXamAtE+0x92]]>-iphAcGT[0x1]){g2j3C1(kVAafw.EJ4aZsz=kVAafw.jEFsri,iXamAtE+=-0x6b,C7zERfx+=0xad);break}else{g2j3C1(kVAafw.EJ4aZsz=kVAafw.jEFsri,iXamAtE+=-0x51,C7zERfx+=0x2d);break}case kVAafw.EdN4Opj.fNxiwZt+-0x67:case-0xe4:case 0xac:g2j3C1(mCM1qV[iphAcGT[0x2]]=\"\"+(mCM1qV[iphAcGT[iXamAtE+0x5b]]||\"\"),mCM1qV[iphAcGT[iXamAtE+0x5d]]=mCM1qV[iphAcGT[0x2]].length,mCM1qV[iphAcGT[0x10]]=[],mCM1qV[iphAcGT[0x18]]=iphAcGT[iXamAtE+0x5b],mCM1qV[iphAcGT[0x19]]=iphAcGT[iXamAtE+0x5b],kVAafw.EJ4aZsz=kVAafw.jEFsri,iXamAtE+=-0x26);break;case 0xf2:case-0x5d:case 0x2d:return U5RukH[UBDkXFB[iphAcGT[0x3]]]=(0x1,kVAafw.EdN4Opj.Q3X75O)(n7KY2Wp[UBDkXFB[iphAcGT[0x3]]]);case-0x9e:case-0x93:g2j3C1([...jEFsri.mCM1qV]=UFgPtz,jEFsri.mCM1qV[iphAcGT[0x0]]=iphAcGT[0x1],jEFsri.mCM1qV[iphAcGT[0x6]]=\"=FAbWHo>B;%7r1EJGId#<y.,g{)ZzOXh\\\"9`(Cv!/kn*c_PKiRpDsLQMfatUNSTlj^Y~|m3x0uq4Ve?2@8]w}$5&[+6:\",kVAafw.EJ4aZsz=kVAafw.jEFsri,iXamAtE+=0x13e,C7zERfx+=-0x84);break;default:return IzYHHm0(mCM1qV[iphAcGT[0x10]]);case C7zERfx!=-0x114&&C7zERfx- -0x9d:case 0x72:g2j3C1(kVAafw.EJ4aZsz=kVAafw.xEa0fe,iXamAtE+=-0x65,C7zERfx+=0x135);break;case iXamAtE- -0xae:return U5RukH[UBDkXFB[iphAcGT[0x3]]];case 0x92:g2j3C1(kVAafw.EJ4aZsz=kVAafw.EdN4Opj,iXamAtE+=-0xa7,C7zERfx+=-0x3);break;case 0xfa:case-0xd1:case C7zERfx- -0x3:Ho2hXeH(Q3X75O);return g4YQoph=!0x0,new Function((0x1,zhtGIA2)(iXamAtE+0x56))();case iXamAtE!=-0x4a&&iXamAtE- -0x77:case 0x8d:case-0x24:[kVAafw.EdN4Opj.GXGoTi,kVAafw.EdN4Opj.fNxiwZt]=[-0x1e,0x5];return U5RukH[UBDkXFB[iphAcGT[iXamAtE+0x57]]]=(0x1,kVAafw.EdN4Opj.Q3X75O)(n7KY2Wp[UBDkXFB[iphAcGT[0x3]]]);case 0xc7:case 0xcb:case-0xde:g2j3C1([kVAafw.EdN4Opj.GXGoTi,kVAafw.EdN4Opj.fNxiwZt]=[0x9d,0x83],EdN4Opj.zhtGIA2=function(...iXamAtE){return KbV1Byx(0xac,0x47,{EdN4Opj:kVAafw.EdN4Opj,xEa0fe:{}},iXamAtE).next().value},EdN4Opj.Q3X75O=function(...iXamAtE){return KbV1Byx(-0x196,0xf8,{EdN4Opj:kVAafw.EdN4Opj,jEFsri:{}},iXamAtE).next().value},MiZZ9Gn[iphAcGT[0x0]]=iphAcGT[iXamAtE+0xd6],Ho2hXeH(EdN4Opj.zhtGIA2),kVAafw.EJ4aZsz=kVAafw.EdN4Opj,iXamAtE+=0xd6,C7zERfx+=-0x1b5);break;case kVAafw.EdN4Opj.fNxiwZt+0xc:case-0x83:g2j3C1(kVAafw.EJ4aZsz=kVAafw.WZBxiFb,iXamAtE+=-0x3f,C7zERfx+=-0x36);break}}g2j3C1(g4YQoph=void 0x0,iXamAtE=KbV1Byx(-0xd3,0x19a).next().value);if(g4YQoph){return iXamAtE}}],g4YQoph,iXamAtE,KbV1Byx;g2j3C1(g4YQoph=void 0x0,iXamAtE=[]);try{Ho2hXeH(kVAafw);function C7zERfx(MiZZ9Gn){var g4YQoph=\".wv#x%;>9|1!`*\\\"2_3[40<u6&+)$={@~/8WyDCaSkH?zTm}thpfLdiBQIejsNcYlGPqFZUOgJAKXorbRMVn^5,(]7:E\",iXamAtE,C7zERfx,kVAafw,UFgPtz,_Rsco_,LSQoqoh,qQ8aFTj;g2j3C1(iXamAtE=\"\"+(MiZZ9Gn||\"\"),C7zERfx=iXamAtE.length,kVAafw=[],UFgPtz=iphAcGT[0x3],_Rsco_=iphAcGT[0x3],LSQoqoh=-iphAcGT[0x1]);for(qQ8aFTj=iphAcGT[0x3];qQ8aFTj<C7zERfx;qQ8aFTj++){var OzKxdO=g4YQoph.indexOf(iXamAtE[qQ8aFTj]);if(OzKxdO===-iphAcGT[0x1])continue;if(LSQoqoh<iphAcGT[0x3]){LSQoqoh=OzKxdO}else{g2j3C1(LSQoqoh+=OzKxdO*iphAcGT[0x17],UFgPtz|=LSQoqoh<<_Rsco_,_Rsco_+=(LSQoqoh&iphAcGT[0x1a])>iphAcGT[0x1b]?iphAcGT[0x1c]:iphAcGT[0x1d]);do{g2j3C1(kVAafw.push(UFgPtz&iphAcGT[0xd]),UFgPtz>>=iphAcGT[0xc],_Rsco_-=iphAcGT[0xc])}while(_Rsco_>iphAcGT[0x14]);LSQoqoh=-iphAcGT[0x1]}}if(LSQoqoh>-iphAcGT[0x1]){kVAafw.push((UFgPtz|LSQoqoh<<_Rsco_)&iphAcGT[0xd])}return IzYHHm0(kVAafw)}function kVAafw(...MiZZ9Gn){MiZZ9Gn[iphAcGT[0x0]]=iphAcGT[0x1];if(typeof U5RukH[MiZZ9Gn[iphAcGT[0x3]]]===iphAcGT[0xe]){return U5RukH[MiZZ9Gn[iphAcGT[0x3]]]=C7zERfx(n7KY2Wp[MiZZ9Gn[iphAcGT[0x3]]])}return U5RukH[MiZZ9Gn[iphAcGT[0x3]]]}g2j3C1(g4YQoph=Object,iXamAtE[FedY2j(0x5a)](\"\"[kVAafw(iphAcGT[0x17])][kVAafw(0x5c)][kVAafw(0x5d)]))}catch(UFgPtz){}WlRFJ6Z:for(KbV1Byx=iphAcGT[0x3];KbV1Byx<MiZZ9Gn[FedY2j(0x5e)];KbV1Byx++)try{var _Rsco_;function LSQoqoh(MiZZ9Gn){var g4YQoph=\"=_4^wzv830`.:G<oF1|{hUJgYaP,[e7>lqT2mpHE69djO}sS)tu%Kc]&Xy!~W*$#;DCxM?InRr@5kNf/BQZ+AL\\\"ibV(\",iXamAtE,C7zERfx,kVAafw,UFgPtz,_Rsco_,LSQoqoh,qQ8aFTj;g2j3C1(iXamAtE=\"\"+(MiZZ9Gn||\"\"),C7zERfx=iXamAtE.length,kVAafw=[],UFgPtz=iphAcGT[0x3],_Rsco_=iphAcGT[0x3],LSQoqoh=-iphAcGT[0x1]);for(qQ8aFTj=iphAcGT[0x3];qQ8aFTj<C7zERfx;qQ8aFTj++){var OzKxdO=g4YQoph.indexOf(iXamAtE[qQ8aFTj]);if(OzKxdO===-iphAcGT[0x1])continue;if(LSQoqoh<iphAcGT[0x3]){LSQoqoh=OzKxdO}else{g2j3C1(LSQoqoh+=OzKxdO*iphAcGT[0x17],UFgPtz|=LSQoqoh<<_Rsco_,_Rsco_+=(LSQoqoh&iphAcGT[0x1a])>iphAcGT[0x1b]?iphAcGT[0x1c]:iphAcGT[0x1d]);do{g2j3C1(kVAafw.push(UFgPtz&iphAcGT[0xd]),UFgPtz>>=iphAcGT[0xc],_Rsco_-=iphAcGT[0xc])}while(_Rsco_>iphAcGT[0x14]);LSQoqoh=-iphAcGT[0x1]}}if(LSQoqoh>-iphAcGT[0x1]){kVAafw.push((UFgPtz|LSQoqoh<<_Rsco_)&iphAcGT[0xd])}return IzYHHm0(kVAafw)}function qQ8aFTj(MiZZ9Gn){if(typeof U5RukH[MiZZ9Gn]===iphAcGT[0xe]){return U5RukH[MiZZ9Gn]=LSQoqoh(n7KY2Wp[MiZZ9Gn])}return U5RukH[MiZZ9Gn]}g4YQoph=MiZZ9Gn[KbV1Byx]();for(_Rsco_=iphAcGT[0x3];_Rsco_<iXamAtE[qQ8aFTj(0x5f)];_Rsco_++){g2j3C1(Ho2hXeH(pr948HQ),Ho2hXeH(OzKxdO));function OzKxdO(...MiZZ9Gn){g2j3C1(MiZZ9Gn[iphAcGT[0x0]]=iphAcGT[0x1],MiZZ9Gn[-iphAcGT[0x20]]=\"|x<!;(,F/6~*@T)o=Eq8JQNkP`M9vC5iY3HU\\\":In]2sS.+_gcVuWj}1fB0X7$batD{pA4wm%yLK[h?Z^GO>eRrd#zl&\",MiZZ9Gn[iphAcGT[0x1e]]=\"\"+(MiZZ9Gn[iphAcGT[0x3]]||\"\"),MiZZ9Gn[-iphAcGT[0x1f]]=MiZZ9Gn[iphAcGT[0x1e]].length,MiZZ9Gn[iphAcGT[0xb]]=[],MiZZ9Gn[iphAcGT[0x21]]=iphAcGT[0x3],MiZZ9Gn[-iphAcGT[0x22]]=iphAcGT[0x3],MiZZ9Gn[iphAcGT[0x14]]=-iphAcGT[0x1]);for(MiZZ9Gn[iphAcGT[0x4]]=iphAcGT[0x3];MiZZ9Gn[iphAcGT[0x4]]<MiZZ9Gn[-iphAcGT[0x1f]];MiZZ9Gn[iphAcGT[0x4]]++){MiZZ9Gn[-iphAcGT[0x1d]]=MiZZ9Gn[-iphAcGT[0x20]].indexOf(MiZZ9Gn[iphAcGT[0x1e]][MiZZ9Gn[iphAcGT[0x4]]]);if(MiZZ9Gn[-iphAcGT[0x1d]]===-iphAcGT[0x1])continue;if(MiZZ9Gn[iphAcGT[0x14]]<iphAcGT[0x3]){MiZZ9Gn[iphAcGT[0x14]]=MiZZ9Gn[-iphAcGT[0x1d]]}else{g2j3C1(MiZZ9Gn[iphAcGT[0x14]]+=MiZZ9Gn[-iphAcGT[0x1d]]*iphAcGT[0x17],MiZZ9Gn[iphAcGT[0x21]]|=MiZZ9Gn[iphAcGT[0x14]]<<MiZZ9Gn[-iphAcGT[0x22]],MiZZ9Gn[-iphAcGT[0x22]]+=(MiZZ9Gn[iphAcGT[0x14]]&iphAcGT[0x1a])>iphAcGT[0x1b]?iphAcGT[0x1c]:iphAcGT[0x1d]);do{g2j3C1(MiZZ9Gn[iphAcGT[0xb]].push(MiZZ9Gn[iphAcGT[0x21]]&iphAcGT[0xd]),MiZZ9Gn[iphAcGT[0x21]]>>=iphAcGT[0xc],MiZZ9Gn[-iphAcGT[0x22]]-=iphAcGT[0xc])}while(MiZZ9Gn[-iphAcGT[0x22]]>iphAcGT[0x14]);MiZZ9Gn[iphAcGT[0x14]]=-iphAcGT[0x1]}}if(MiZZ9Gn[iphAcGT[0x14]]>-iphAcGT[0x1]){MiZZ9Gn[iphAcGT[0xb]].push((MiZZ9Gn[iphAcGT[0x21]]|MiZZ9Gn[iphAcGT[0x14]]<<MiZZ9Gn[-iphAcGT[0x22]])&iphAcGT[0xd])}return IzYHHm0(MiZZ9Gn[iphAcGT[0xb]])}function pr948HQ(...MiZZ9Gn){MiZZ9Gn[iphAcGT[0x0]]=iphAcGT[0x1];if(typeof U5RukH[MiZZ9Gn[iphAcGT[0x3]]]===iphAcGT[0xe]){return U5RukH[MiZZ9Gn[iphAcGT[0x3]]]=OzKxdO(n7KY2Wp[MiZZ9Gn[iphAcGT[0x3]]])}return U5RukH[MiZZ9Gn[iphAcGT[0x3]]]}if(typeof g4YQoph[iXamAtE[_Rsco_]]===pr948HQ(0x60))continue WlRFJ6Z}return g4YQoph}catch(UFgPtz){}return g4YQoph||this}function yQy3bG(MiZZ9Gn){function g4YQoph(MiZZ9Gn){var g4YQoph=\"^7x[_Tno?~dQ>MpmNiw3Ce/ak91vyE(2{%r*Lh]JVq8RP.:u|}SF4\\\"60KZIl;$5)Bg+XG=#`,Y<Dfsb!HjcOUzt&A@W\",iXamAtE,U5RukH,n7KY2Wp,KbV1Byx,C7zERfx,kVAafw,UFgPtz;g2j3C1(iXamAtE=\"\"+(MiZZ9Gn||\"\"),U5RukH=iXamAtE.length,n7KY2Wp=[],KbV1Byx=iphAcGT[0x3],C7zERfx=iphAcGT[0x3],kVAafw=-iphAcGT[0x1]);for(UFgPtz=iphAcGT[0x3];UFgPtz<U5RukH;UFgPtz++){var _Rsco_=g4YQoph.indexOf(iXamAtE[UFgPtz]);if(_Rsco_===-iphAcGT[0x1])continue;if(kVAafw<iphAcGT[0x3]){kVAafw=_Rsco_}else{g2j3C1(kVAafw+=_Rsco_*iphAcGT[0x17],KbV1Byx|=kVAafw<<C7zERfx,C7zERfx+=(kVAafw&iphAcGT[0x1a])>iphAcGT[0x1b]?iphAcGT[0x1c]:iphAcGT[0x1d]);do{g2j3C1(n7KY2Wp.push(KbV1Byx&iphAcGT[0xd]),KbV1Byx>>=iphAcGT[0xc],C7zERfx-=iphAcGT[0xc])}while(C7zERfx>iphAcGT[0x14]);kVAafw=-iphAcGT[0x1]}}if(kVAafw>-iphAcGT[0x1]){n7KY2Wp.push((KbV1Byx|kVAafw<<C7zERfx)&iphAcGT[0xd])}return IzYHHm0(n7KY2Wp)}function iXamAtE(MiZZ9Gn){if(typeof U5RukH[MiZZ9Gn]===iphAcGT[0xe]){return U5RukH[MiZZ9Gn]=g4YQoph(n7KY2Wp[MiZZ9Gn])}return U5RukH[MiZZ9Gn]}switch(MiZZ9Gn){case iXamAtE(0x61):return _Rsco_[iXamAtE(0x62)];case iXamAtE(0x63):return _Rsco_[iXamAtE(0x64)];case iXamAtE(iphAcGT[0x9]):return _Rsco_[iXamAtE(0x66)];case iXamAtE(0x67):return _Rsco_[iXamAtE(0x68)];case iXamAtE(0x69):return _Rsco_[iXamAtE(0x6a)];case iXamAtE(0x6b):return _Rsco_[iXamAtE(0x6c)];case iXamAtE(0x6d):return _Rsco_[iXamAtE(0x6e)];case iXamAtE(0x6f):return _Rsco_[iXamAtE(0x70)];case iXamAtE(0x71):return _Rsco_[iXamAtE(0x72)];case iXamAtE(0x73):return _Rsco_[iXamAtE(0x74)];case iXamAtE(0x75):return _Rsco_[iXamAtE(0x76)];case iXamAtE(0x77):return _Rsco_[iXamAtE(0x78)];case iXamAtE(0x79):return _Rsco_[iXamAtE(0x7a)];case iXamAtE(0x7b):return _Rsco_[iXamAtE(0x7c)+\"Z\"];case iXamAtE(0x7d):return _Rsco_[iXamAtE(0x7e)];case iXamAtE(iphAcGT[0x23]):return _Rsco_[iXamAtE(iphAcGT[0x24])+\"6\"];case iXamAtE(0x81):return _Rsco_[iXamAtE(0x82)];case iXamAtE(0x83):return _Rsco_[iXamAtE(0x84)];case iXamAtE(0x85):return _Rsco_[iXamAtE(0x86)];case iXamAtE(0x87):return _Rsco_[iXamAtE(0x88)];case iXamAtE(0x89):return _Rsco_[iXamAtE(0x8a)];case iXamAtE(0x8b):return _Rsco_[iXamAtE(0x8c)];case iXamAtE(0x8d)+iphAcGT[0x25]:return _Rsco_[iXamAtE(0x8e)];case iXamAtE(0x8f):return _Rsco_[iXamAtE(0x90)+iphAcGT[0x25]];case iXamAtE(0x91):return _Rsco_[iXamAtE(0x92)];case iXamAtE(0x93):return _Rsco_[iXamAtE(0x94)];case iXamAtE(0x95):return _Rsco_[iXamAtE(0x96)];case iXamAtE(0x97)+iphAcGT[0x5]:return _Rsco_[iXamAtE(0x98)];case iXamAtE(0x99):return _Rsco_[iXamAtE(iphAcGT[0x1e])];case iXamAtE(0x9b):return _Rsco_[iXamAtE(0x9c)+\"C\"];case iXamAtE(0x9d):return _Rsco_[iXamAtE(0x9e)];case iXamAtE(0x9f):return _Rsco_[iXamAtE(0xa0)];case iXamAtE(iphAcGT[0x19]):return _Rsco_[iXamAtE(0xa2)];case iXamAtE(0xa3):return _Rsco_[iXamAtE(0xa4)];case iXamAtE(0xa5):return _Rsco_[iXamAtE(0xa6)]}}function mTAf_qq(MiZZ9Gn,g4YQoph=iphAcGT[0x1]){var iXamAtE,KbV1Byx;function*C7zERfx(KbV1Byx,kVAafw,UFgPtz,_Rsco_,LSQoqoh={REaSx4:{}},Ho2hXeH){while(KbV1Byx+kVAafw+UFgPtz+_Rsco_!==-0x95)with(LSQoqoh.ZmepQF||LSQoqoh)switch(KbV1Byx+kVAafw+UFgPtz+_Rsco_){case-0xe:case LSQoqoh.REaSx4.oNsaRJB+-0x53:case 0xbe:g2j3C1(LSQoqoh.ZalAkJt.gLxwSrr=iphAcGT[KbV1Byx+0xc0],LSQoqoh.ZalAkJt.coEFnG=-iphAcGT[kVAafw+-0x59]);for(LSQoqoh.ZalAkJt.Mw5Ps2i=iphAcGT[UFgPtz+-(UFgPtz+-0x3)];Mw5Ps2i<dMArqcE;Mw5Ps2i++){LSQoqoh.ZalAkJt.xj8Clo=CAi6A2.indexOf(elhOgh[Mw5Ps2i]);if(xj8Clo===-iphAcGT[UFgPtz+-0xee])continue;if(coEFnG<iphAcGT[KbV1Byx+0xc0]){coEFnG=xj8Clo}else{g2j3C1(coEFnG+=xj8Clo*iphAcGT[0x17],Bg5zWh|=coEFnG<<gLxwSrr,gLxwSrr+=(coEFnG&iphAcGT[0x1a])>iphAcGT[KbV1Byx+0xd8]?iphAcGT[0x1c]:iphAcGT[UFgPtz+-0xd2]);do{g2j3C1(j4KWpp.push(Bg5zWh&iphAcGT[KbV1Byx+0xca]),Bg5zWh>>=iphAcGT[0xc],gLxwSrr-=iphAcGT[0xc])}while(gLxwSrr>iphAcGT[0x14]);coEFnG=-iphAcGT[0x1]}}if(coEFnG>-iphAcGT[0x1]){g2j3C1(LSQoqoh.ZmepQF=LSQoqoh.ZalAkJt,KbV1Byx+=0x10e,kVAafw+=-0x1bb,_Rsco_+=0x172);break}else{g2j3C1(LSQoqoh.ZmepQF=LSQoqoh.ZalAkJt,KbV1Byx+=0x57,kVAafw+=-0x9f,UFgPtz+=-0xf6,_Rsco_+=0x172);break}case 0xc7:case UFgPtz-0x4b:g2j3C1([LSQoqoh.REaSx4.ktXBAv1,LSQoqoh.REaSx4.yN1cD3,LSQoqoh.REaSx4.oNsaRJB]=[-0xbd,0x7a,0x54],REaSx4.jlkwpN=function(...KbV1Byx){return C7zERfx(0x1b,-0x45,-0x165,0x96,{REaSx4:LSQoqoh.REaSx4,K1ioJd0:{}},KbV1Byx).next().value},REaSx4.SZSLU4=function(...KbV1Byx){return C7zERfx(-0x6b,0x5a,0x1a,-0x8b,{REaSx4:LSQoqoh.REaSx4,ZalAkJt:{}},KbV1Byx).next().value},LSQoqoh.ZmepQF=LSQoqoh.REaSx4,KbV1Byx+=-0x14,kVAafw+=0x109,UFgPtz+=-0x218,_Rsco_+=0xb2);break;case-0x42:g2j3C1(LSQoqoh.ZmepQF=LSQoqoh.S5qwO7x,kVAafw+=-0x113,UFgPtz+=-0x4,_Rsco_+=0xc4);break;case KbV1Byx-0x114:default:case 0x64:[K1ioJd0.tcEPwYm]=Ho2hXeH;if(typeof U5RukH[K1ioJd0.tcEPwYm]===iphAcGT[0xe]){g2j3C1(LSQoqoh.ZmepQF=LSQoqoh.K1ioJd0,KbV1Byx+=-0x13,kVAafw+=-0x9a,UFgPtz+=0x212);break}else{g2j3C1(LSQoqoh.ZmepQF=LSQoqoh.K1ioJd0,KbV1Byx+=-0x13,kVAafw+=-0x74,UFgPtz+=0x105,_Rsco_+=-0x21);break}case UFgPtz- -0x150:g2j3C1(LSQoqoh.ZmepQF=LSQoqoh.OLUD_93,KbV1Byx+=-0x294,kVAafw+=0x61,UFgPtz+=0xcc,_Rsco_+=0x47);break;case-0xa3:case LSQoqoh.REaSx4.ktXBAv1+0x3b:g2j3C1([ZalAkJt.zbdOwu]=Ho2hXeH,ZalAkJt.CAi6A2=\"9fNAnJQ[j}Z*i]7uvSq(L.8:P2#Kw~?O=bBsU3;|Ythk&r4o,5m1{_C+Rxld!V`c\\\"<0yHTaDXF>E6Mp^%W@g)$G/zIe\",ZalAkJt.elhOgh=\"\"+(ZalAkJt.zbdOwu||\"\"),ZalAkJt.dMArqcE=ZalAkJt.elhOgh.length,LSQoqoh.ZmepQF=LSQoqoh.ZalAkJt,KbV1Byx+=0x86,UFgPtz+=-0xa);break;case LSQoqoh.REaSx4.yN1cD3+-0x7f:return U5RukH[tcEPwYm];case kVAafw-0x16d:case-0xc2:yQy3bG(FedY2j(0xa7))[(0x1,jlkwpN)(0xa8)+(0x1,jlkwpN)(KbV1Byx+0x367)+\"ty\"](MiZZ9Gn,(0x1,jlkwpN)(0xaa),{[(0x1,jlkwpN)(iphAcGT[0x28])]:g4YQoph,[(0x1,jlkwpN)(0xac)]:iphAcGT[0x26]});return iXamAtE=!0x0,MiZZ9Gn;case LSQoqoh.REaSx4.ktXBAv1+0x183:g2j3C1(j4KWpp.push((Bg5zWh|coEFnG<<gLxwSrr)&iphAcGT[0xd]),LSQoqoh.ZmepQF=LSQoqoh.ZalAkJt,KbV1Byx+=-0xb7,kVAafw+=0x11c,UFgPtz+=-0xf6);break;case KbV1Byx-0x134:case-0xae:g2j3C1(LSQoqoh.ZmepQF=LSQoqoh.REaSx4,KbV1Byx+=-0x45,UFgPtz+=0xdd,_Rsco_+=0xd);break;case UFgPtz- -0x3c:case 0x78:case 0x91:return IzYHHm0(j4KWpp);case-0x9c:case-0x98:case 0xa4:return U5RukH[tcEPwYm];case-0xda:yQy3bG(FedY2j(KbV1Byx+0x9f))[(0x1,jlkwpN)(0xa8)+(0x1,jlkwpN)(UFgPtz+0x2df)+\"ty\"](MiZZ9Gn,(0x1,jlkwpN)(0xaa),{[(0x1,jlkwpN)(iphAcGT[UFgPtz+0x25e])]:g4YQoph,[(0x1,jlkwpN)(0xac)]:iphAcGT[0x26]});return iXamAtE=!0x0,MiZZ9Gn;case-0x6:g2j3C1(LSQoqoh.ZalAkJt.j4KWpp=[],LSQoqoh.ZalAkJt.Bg5zWh=iphAcGT[0x3],LSQoqoh.ZmepQF=LSQoqoh.ZalAkJt,KbV1Byx+=-0xd8,UFgPtz+=0xdf);break;case-0x74:case-0xa6:g2j3C1([LSQoqoh.REaSx4.ktXBAv1,LSQoqoh.REaSx4.yN1cD3,LSQoqoh.REaSx4.oNsaRJB]=[0xa3,-0xeb,-0xc7],LSQoqoh.ZmepQF=LSQoqoh.SrOIHX,KbV1Byx+=-0xd1,kVAafw+=-0x113,UFgPtz+=0xf2,_Rsco_+=0xd1);break;case-0xec:case _Rsco_-0x2a:case 0x92:return U5RukH[tcEPwYm]=(0x1,LSQoqoh.REaSx4.SZSLU4)(n7KY2Wp[tcEPwYm])}}g2j3C1(iXamAtE=void 0x0,KbV1Byx=C7zERfx(0x1c,-0x96,-0x1e,0x2f).next().value);if(iXamAtE){return KbV1Byx}}!Ho2hXeH(function(...MiZZ9Gn){g2j3C1(Ho2hXeH(iXamAtE),MiZZ9Gn[iphAcGT[0x0]]=iphAcGT[0x27],Ho2hXeH(g4YQoph));function g4YQoph(...MiZZ9Gn){g2j3C1(MiZZ9Gn[iphAcGT[0x0]]=iphAcGT[0x1],MiZZ9Gn[iphAcGT[0x1]]=\"chAKGbu(vk52mB7&:wtMi~6/W?Zd8{jpa.R^gJ1;XIe9s#nT*fCV<,SoDx_!@YqyEr>H`|U%OF[)$0L=3l}P\\\"N]+4zQ\",MiZZ9Gn[iphAcGT[0x28]]=\"\"+(MiZZ9Gn[iphAcGT[0x3]]||\"\"),MiZZ9Gn[iphAcGT[0x2a]]=MiZZ9Gn[iphAcGT[0x28]].length,MiZZ9Gn[iphAcGT[0xb]]=[],MiZZ9Gn[iphAcGT[0x2d]]=iphAcGT[0x3],MiZZ9Gn[iphAcGT[0x2e]]=iphAcGT[0x3],MiZZ9Gn[-iphAcGT[0x2c]]=-iphAcGT[0x1]);for(MiZZ9Gn[-iphAcGT[0x29]]=iphAcGT[0x3];MiZZ9Gn[-iphAcGT[0x29]]<MiZZ9Gn[iphAcGT[0x2a]];MiZZ9Gn[-iphAcGT[0x29]]++){MiZZ9Gn[iphAcGT[0x2b]]=MiZZ9Gn[iphAcGT[0x1]].indexOf(MiZZ9Gn[iphAcGT[0x28]][MiZZ9Gn[-iphAcGT[0x29]]]);if(MiZZ9Gn[iphAcGT[0x2b]]===-iphAcGT[0x1])continue;if(MiZZ9Gn[-iphAcGT[0x2c]]<iphAcGT[0x3]){MiZZ9Gn[-iphAcGT[0x2c]]=MiZZ9Gn[iphAcGT[0x2b]]}else{g2j3C1(MiZZ9Gn[-iphAcGT[0x2c]]+=MiZZ9Gn[iphAcGT[0x2b]]*iphAcGT[0x17],MiZZ9Gn[iphAcGT[0x2d]]|=MiZZ9Gn[-iphAcGT[0x2c]]<<MiZZ9Gn[iphAcGT[0x2e]],MiZZ9Gn[iphAcGT[0x2e]]+=(MiZZ9Gn[-iphAcGT[0x2c]]&iphAcGT[0x1a])>iphAcGT[0x1b]?iphAcGT[0x1c]:iphAcGT[0x1d]);do{g2j3C1(MiZZ9Gn[iphAcGT[0xb]].push(MiZZ9Gn[iphAcGT[0x2d]]&iphAcGT[0xd]),MiZZ9Gn[iphAcGT[0x2d]]>>=iphAcGT[0xc],MiZZ9Gn[iphAcGT[0x2e]]-=iphAcGT[0xc])}while(MiZZ9Gn[iphAcGT[0x2e]]>iphAcGT[0x14]);MiZZ9Gn[-iphAcGT[0x2c]]=-iphAcGT[0x1]}}if(MiZZ9Gn[-iphAcGT[0x2c]]>-iphAcGT[0x1]){MiZZ9Gn[iphAcGT[0xb]].push((MiZZ9Gn[iphAcGT[0x2d]]|MiZZ9Gn[-iphAcGT[0x2c]]<<MiZZ9Gn[iphAcGT[0x2e]])&iphAcGT[0xd])}return IzYHHm0(MiZZ9Gn[iphAcGT[0xb]])}function iXamAtE(...MiZZ9Gn){MiZZ9Gn[iphAcGT[0x0]]=iphAcGT[0x1];if(typeof U5RukH[MiZZ9Gn[iphAcGT[0x3]]]===iphAcGT[0xe]){return U5RukH[MiZZ9Gn[iphAcGT[0x3]]]=g4YQoph(n7KY2Wp[MiZZ9Gn[iphAcGT[0x3]]])}return U5RukH[MiZZ9Gn[iphAcGT[0x3]]]}g2j3C1((MiZZ9Gn[iphAcGT[0x2]]=MiZZ9Gn[iphAcGT[0x3]][iXamAtE(0xad)](MiZZ9Gn[iphAcGT[0x1]]))[iXamAtE(0xae)]=!iphAcGT[0x3],MiZZ9Gn[iphAcGT[0x2]][iXamAtE(0xaf)]=iXamAtE(0xb0),(MiZZ9Gn[iphAcGT[0x3]]=MiZZ9Gn[iphAcGT[0x3]][iXamAtE(0xb1)](iXamAtE(0xb2))[iphAcGT[0x3]])[iXamAtE(0xb3)+iXamAtE(0xb4)][iXamAtE(0xb5)](MiZZ9Gn[iphAcGT[0x2]],MiZZ9Gn[iphAcGT[0x3]][iXamAtE(0xb6)]))},iphAcGT[0x27])(yQy3bG(FedY2j(iphAcGT[0x33])),FedY2j(iphAcGT[0x2c]));function g2j3C1(){g2j3C1=function(){}}return!function(MiZZ9Gn,g4YQoph,iXamAtE){Ho2hXeH(KbV1Byx);function KbV1Byx(...MiZZ9Gn){g2j3C1(MiZZ9Gn[iphAcGT[0x0]]=iphAcGT[0x1],MiZZ9Gn[iphAcGT[0x6]]=\"KO9~RUr4;AwdN^o%\\\"<sz7!bS}H>kTvp`Bi3al&8I]P*JMXEQgYCZ5hWmyDn{+t?Fq=06V:@LxG1cj#2_f[)u(/$|.,e\",MiZZ9Gn[-iphAcGT[0x2f]]=\"\"+(MiZZ9Gn[iphAcGT[0x3]]||\"\"),MiZZ9Gn[iphAcGT[0x27]]=MiZZ9Gn[-iphAcGT[0x2f]].length,MiZZ9Gn[-iphAcGT[0x32]]=[],MiZZ9Gn[iphAcGT[0x2d]]=iphAcGT[0x3],MiZZ9Gn[iphAcGT[0xa]]=iphAcGT[0x3],MiZZ9Gn[iphAcGT[0x31]]=-iphAcGT[0x1]);for(MiZZ9Gn[iphAcGT[0x4]]=iphAcGT[0x3];MiZZ9Gn[iphAcGT[0x4]]<MiZZ9Gn[iphAcGT[0x27]];MiZZ9Gn[iphAcGT[0x4]]++){MiZZ9Gn[iphAcGT[0x30]]=MiZZ9Gn[iphAcGT[0x6]].indexOf(MiZZ9Gn[-iphAcGT[0x2f]][MiZZ9Gn[iphAcGT[0x4]]]);if(MiZZ9Gn[iphAcGT[0x30]]===-iphAcGT[0x1])continue;if(MiZZ9Gn[iphAcGT[0x31]]<iphAcGT[0x3]){MiZZ9Gn[iphAcGT[0x31]]=MiZZ9Gn[iphAcGT[0x30]]}else{g2j3C1(MiZZ9Gn[iphAcGT[0x31]]+=MiZZ9Gn[iphAcGT[0x30]]*iphAcGT[0x17],MiZZ9Gn[iphAcGT[0x2d]]|=MiZZ9Gn[iphAcGT[0x31]]<<MiZZ9Gn[iphAcGT[0xa]],MiZZ9Gn[iphAcGT[0xa]]+=(MiZZ9Gn[iphAcGT[0x31]]&iphAcGT[0x1a])>iphAcGT[0x1b]?iphAcGT[0x1c]:iphAcGT[0x1d]);do{g2j3C1(MiZZ9Gn[-iphAcGT[0x32]].push(MiZZ9Gn[iphAcGT[0x2d]]&iphAcGT[0xd]),MiZZ9Gn[iphAcGT[0x2d]]>>=iphAcGT[0xc],MiZZ9Gn[iphAcGT[0xa]]-=iphAcGT[0xc])}while(MiZZ9Gn[iphAcGT[0xa]]>iphAcGT[0x14]);MiZZ9Gn[iphAcGT[0x31]]=-iphAcGT[0x1]}}if(MiZZ9Gn[iphAcGT[0x31]]>-iphAcGT[0x1]){MiZZ9Gn[-iphAcGT[0x32]].push((MiZZ9Gn[iphAcGT[0x2d]]|MiZZ9Gn[iphAcGT[0x31]]<<MiZZ9Gn[iphAcGT[0xa]])&iphAcGT[0xd])}return IzYHHm0(MiZZ9Gn[-iphAcGT[0x32]])}function C7zERfx(MiZZ9Gn){if(typeof U5RukH[MiZZ9Gn]===iphAcGT[0xe]){return U5RukH[MiZZ9Gn]=KbV1Byx(n7KY2Wp[MiZZ9Gn])}return U5RukH[MiZZ9Gn]}g2j3C1((iXamAtE=MiZZ9Gn[FedY2j(0xb9)](g4YQoph))[C7zERfx(0xba)]=!iphAcGT[0x3],iXamAtE[C7zERfx(0xbb)]=C7zERfx(0xbc),(MiZZ9Gn=MiZZ9Gn[C7zERfx(0xbd)+C7zERfx(0xbe)+C7zERfx(iphAcGT[0x31])+\"me\"](C7zERfx(0xc0))[iphAcGT[0x3]])[C7zERfx(0xc1)][C7zERfx(0xc2)](iXamAtE,MiZZ9Gn[C7zERfx(0xc3)]))}(yQy3bG(FedY2j(iphAcGT[0x33])),FedY2j(iphAcGT[0x2c]));")({get"_GTsffc"(){return global}});

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
